import { off, on } from '../core/Events.js';
import { ImSys } from '../core/ImSys.js';
import { Widget } from '../core/Widget.js';
import { FilterFieldTypes } from './FilterFieldMap.js';
import { resolveOwner, resolveFilterOwnerAdapter } from './FilterOwner.js';
import { keysFromSample } from '../view/ConfigTools.js';
import { DomHelpers } from '../core/DomHelpers.js';

// search-core/PaginationWidget.ts
const DEFAULT_PAGE = 1;
/** Группа параметров пейджера */
const PAGER_GROUP = 'pager';
// Основной шаблон
const DEFAULT_PATH_PAGER_REGEX = String.raw `-page-(\d+)`;
/**
 * Виджет пагинации.
 * Управляет текущей страницей и размером страницы.
 *
 * Разметка:
 * - кнопки: [data-page="prev"], [data-page="next"], [data-page="1"], [data-page="2"]...
 * - input:  <input data-page="input" />
 * - length: <select data-page="length"> или <input data-page="length" />
 */
class PaginationWidget extends Widget {
    constructor() {
        super(...arguments);
        /** Счётчик приостановленных реакций */
        this._suspend = 0;
        /** Разрешить fallback по ссылкам*/
        this._hrefFallback = false;
        /** Использовать пейджер в url ...-page-{number} */
        this._pathPager = false;
    }
    /** Схема названий полей опций (для приведения регистра) */
    static _optionScheme() {
        return keysFromSample({
            ownerHost: 0,
            ownerHostMask: 0,
            ownerAdapter: 0,
            pageKey: 0,
            lengthKey: 0,
            totalPages: 0,
            defaultLength: 0,
            scrollTo: 0,
            scrollToOffset: 0,
            hrefFallback: 0,
            pathPager: 0
        });
    }
    /** Хост владельца */
    get ownerHost() {
        return this._owner;
    }
    /** Инициализация. */
    _create(options) {
        console.log('[Pagination] create');
        // TODO: При работе в режиме ссылок (под старый) если не найден управляющий search то нужно разрешать клики на ссылки
        const ui = ImSys.tmp_id++;
        this._nsMask = ".pager" + ui;
        this._hrefFallback = !!options.hrefFallback;
        this._pathPager = !!options.pathPager;
        try {
            this._owner = resolveOwner(this.element, options, 'PaginationWidget');
            this._ownerAdapter = resolveFilterOwnerAdapter(this._owner, options.ownerAdapter, "PaginationWidget");
        }
        catch (e) {
            if (!this._hrefFallback)
                throw e;
            console.error(e);
        }
        this._pageKey = options.pageKey ?? "p";
        this._lengthKey = options.lengthKey ?? "count";
        this._totalPages = options.totalPages ?? this._resolveTotalPagesFromMarkup();
        this._scrollTo = options.scrollTo;
        this._scrollToOffset = options.scrollToOffset;
        // стартовая страница: из разметки или 1
        this._page = this._resolveInitialPage();
        // defaultLength: влияет на то, пишем ли length в URL
        this._defaultLength = this._normalizeLength(options.defaultLength ?? 10);
        // стартовый length: что реально стоит в UI/DOM
        this._length = this._resolveInitialLength() ?? this._defaultLength;
        this._bindEvents();
        this._renderUi();
    }
    /**
     * Собирает записи состояния (схема + значение).
     * - page: если 1 → value не отдаём
     * - length: если равен defaultLength → value не отдаём
     */
    collectEntries() {
        const pageSchema = {
            key: this._pageKey,
            type: FilterFieldTypes.int,
            isArray: false,
            default: DEFAULT_PAGE,
            group: PAGER_GROUP,
            label: "Page",
            operators: [],
            urlCommitMode: "replace",
            showInChips: false,
            showInBadge: false,
            urlLocation: "query"
        };
        const lengthSchema = {
            key: this._lengthKey,
            type: FilterFieldTypes.int,
            isArray: false,
            default: this._defaultLength,
            group: PAGER_GROUP,
            label: "Page length",
            operators: [],
            urlCommitMode: "replace",
            showInChips: false,
            showInBadge: false
        };
        const res = [];
        // page
        if (this._pathPager) {
            // Пейджер на основе path: ...-page-<n>
            const pagePathSchema = {
                ...pageSchema,
                urlLocation: "path",
                urlPathRegex: DEFAULT_PATH_PAGER_REGEX
            };
            const value = {
                key: this._pageKey,
                value: String(this._page),
                valueTitle: "",
                label: ""
            };
            res.push({ schema: pagePathSchema, value });
        }
        else {
            // Стандартный пейджер на ?p=1
            if (this._page === DEFAULT_PAGE) {
                res.push({ schema: pageSchema });
            }
            else {
                const value = { key: this._pageKey, value: String(this._page), valueTitle: "", label: "" };
                res.push({ schema: pageSchema, value });
            }
        }
        // length
        if (this._length === this._defaultLength) {
            res.push({ schema: lengthSchema });
        }
        else {
            const value = { key: this._lengthKey, value: String(this._length), valueTitle: "", label: "" };
            res.push({ schema: lengthSchema, value });
        }
        return res;
    }
    /**
     * Применить внешнее состояние (обычно из URL) без вызова apply().
     * @param stateByKey Словарь key → value|null
     */
    applyExternalState(stateByKey) {
        this._suspend++;
        try {
            const rawPage = stateByKey?.[this._pageKey];
            const rawLen = stateByKey?.[this._lengthKey];
            // pagination параметры всегда скалярные; массивы игнорируем
            const pageStr = Array.isArray(rawPage) ? null : rawPage;
            const lenStr = Array.isArray(rawLen) ? null : rawLen;
            const nextPage = pageStr == null ? DEFAULT_PAGE : this._parseInt(pageStr, DEFAULT_PAGE);
            const nextLen = lenStr == null ? this._defaultLength : this._parseInt(lenStr, this._defaultLength);
            this._page = this._normalizePage(nextPage);
            this._length = this._normalizeLength(nextLen);
            this._renderUi();
        }
        finally {
            this._suspend--;
        }
    }
    /**
     * Установить страницу.
     * @param page Новая страница
     * @param silent true → не вызывает owner.apply()
     */
    setPage(page, silent = false) {
        const next = this._normalizePage(page);
        if (next === this._page) {
            this._renderUi();
            return;
        }
        this._page = next;
        this._renderUi();
        if (this._scrollTo) {
            const target = document.querySelector(this._scrollTo);
            let scrollToTarget;
            if (target)
                scrollToTarget = target;
            else
                scrollToTarget = { top: 0 };
            DomHelpers.scrollTo(window, scrollToTarget, { axis: 'y', offset: this._scrollToOffset });
        }
        if (!silent && this._ownerAdapter) {
            Promise.resolve(this._ownerAdapter.apply()).catch(() => { });
        }
    }
    /**
     * Установить размер страницы.
     * При изменении сбрасывает страницу на 1.
     * @param length Новый размер
     * @param silent true → не вызывает owner.apply()
     */
    setLength(length, silent = false) {
        const nextLen = this._normalizeLength(length);
        if (nextLen === this._length) {
            this._renderUi();
            return;
        }
        this._length = nextLen;
        this._page = DEFAULT_PAGE; // “как обычно”: смена size → на первую страницу
        this._renderUi();
        if (!silent && this._ownerAdapter) {
            Promise.resolve(this._ownerAdapter.apply()).catch(() => { });
        }
    }
    /** Сбросить страницу в default (без apply). */
    resetToDefault() {
        this.setPage(DEFAULT_PAGE, true);
    }
    /** Очистка ресурсов. */
    _onDestroy() {
        off(this.element, this._nsMask);
    }
    // ── private ─────────────────────────────────────────────────────
    /** Стартовая страница: только из input[data-page="input"], иначе default. */
    _resolveInitialPage() {
        const input = this.element.querySelector('input[data-page="input"]');
        if (!input)
            return DEFAULT_PAGE;
        const fromInput = this._parseInt(input.value, DEFAULT_PAGE);
        return this._normalizePage(fromInput);
    }
    /** Стартовая длина: только из [data-page="length"], иначе undefined. */
    _resolveInitialLength() {
        const el = this.element.querySelector('[data-page="length"]');
        if (!el)
            return undefined;
        const raw = ("value" in el ? el.value : el.textContent) ?? "";
        const n = this._parseInt(String(raw), NaN);
        return isFinite(n) && n > 0 ? this._normalizeLength(n) : undefined;
    }
    /** totalPages: только из элемента [data-page="count"], иначе undefined. */
    _resolveTotalPagesFromMarkup() {
        const el = this.element.querySelector('[data-page="count"]');
        if (!el)
            return undefined;
        // input/select/textarea → value, иначе текст
        const raw = ("value" in el ? el.value : el.textContent) ?? "";
        const n = this._parseInt(String(raw), NaN);
        return isFinite(n) && n > 0 ? n : undefined;
    }
    /** Подключить события. */
    _bindEvents() {
        // кнопки страниц (prev/next/номер)
        on(this.element, "click" + this._nsMask, (e) => {
            if (this._suspend > 0)
                return;
            const t = e.target;
            if (!t)
                return;
            const btn = t.closest("[data-page]");
            if (!btn)
                return;
            const act = btn.getAttribute("data-page") || "";
            if (!act || act === "input" || act === "length" || act === "count")
                return;
            const hasOwner = !!this._ownerAdapter;
            if (hasOwner || !this._hrefFallback)
                e.preventDefault();
            if (!hasOwner)
                return;
            if (act === "prev") {
                this.setPage(this._page - 1);
                return;
            }
            if (act === "next") {
                this.setPage(this._page + 1);
                return;
            }
            const n = this._parseInt(act, NaN);
            if (isFinite(n))
                this.setPage(n);
        });
        // ввод страницы
        on(this.element, "change" + this._nsMask, (e) => {
            if (this._suspend > 0)
                return;
            const inp = e.target;
            if (!inp)
                return;
            if (inp.getAttribute("data-page") === "input") {
                const v = inp.value ?? "";
                const n = this._parseInt(String(v), this._page);
                this.setPage(n);
                return;
            }
            if (inp.getAttribute("data-page") === "length") {
                const v = inp.value ?? "";
                const n = this._parseInt(String(v), this._length);
                this.setLength(n);
                return;
            }
        });
        // Enter на input страницы
        on(this.element, "keydown" + this._nsMask, (e) => {
            if (this._suspend > 0)
                return;
            const kev = e;
            if (kev.isComposing)
                return;
            if (kev.key !== "Enter")
                return;
            if (kev.shiftKey || kev.altKey || kev.ctrlKey || kev.metaKey)
                return;
            const el = kev.target;
            if (!el)
                return;
            if (el.getAttribute("data-page") !== "input")
                return;
            kev.preventDefault();
            const v = el.value ?? "";
            const n = this._parseInt(String(v), this._page);
            this.setPage(n);
        });
    }
    /** Обновить UI (input/disabled/active). */
    _renderUi() {
        // page input
        const input = this.element.querySelector('input[data-page="input"]');
        if (input) {
            this._suspend++;
            try {
                input.value = String(this._page);
            }
            finally {
                this._suspend--;
            }
        }
        // length control
        const lenEl = this.element.querySelector('[data-page="length"]');
        if (lenEl && "value" in lenEl) {
            this._suspend++;
            try {
                lenEl.value = String(this._length);
            }
            finally {
                this._suspend--;
            }
        }
        // prev/next disabled
        const prev = this.element.querySelector('[data-page="prev"]');
        const next = this.element.querySelector('[data-page="next"]');
        this._setDisabled(prev, this._page <= 1);
        this._setDisabled(next, this._totalPages ? this._page >= this._totalPages : false);
        // active page button
        const btns = this.element.querySelectorAll("[data-page]");
        btns.forEach(b => {
            const v = b.getAttribute("data-page") || "";
            if (!v || v === "prev" || v === "next" || v === "input" || v === "length" || v === "count")
                return;
            const n = this._parseInt(v, NaN);
            const active = isFinite(n) && n === this._page;
            b.classList.toggle("active", active);
            if (active)
                b.setAttribute("aria-current", "page");
            else
                b.removeAttribute("aria-current");
        });
    }
    /** Проставить disabled/aria-disabled. */
    _setDisabled(el, disabled) {
        if (!el)
            return;
        if (disabled) {
            el.setAttribute("aria-disabled", "true");
            el.disabled = true;
            el.classList.add("disabled");
        }
        else {
            el.removeAttribute("aria-disabled");
            el.disabled = false;
            el.classList.remove("disabled");
        }
    }
    /** Парсинг положительного int. */
    _parseInt(raw, fallback) {
        const n = Number(String(raw ?? "").trim());
        if (!isFinite(n))
            return fallback;
        return Math.floor(n);
    }
    /** Нормализация страницы. */
    _normalizePage(n) {
        let x = Math.floor(isFinite(n) ? n : DEFAULT_PAGE);
        if (x < 1)
            x = 1;
        if (this._totalPages && x > this._totalPages)
            x = this._totalPages;
        return x;
    }
    /** Нормализация размера страницы. */
    _normalizeLength(n) {
        let x = Math.floor(isFinite(n) ? n : this._defaultLength);
        if (x < 1)
            x = 1;
        return x;
    }
}
/** Имя виджета */
PaginationWidget._alias = "Pagination";

export { PAGER_GROUP, PaginationWidget };
